/**
 * Make a club-supplied SVG safe to inline.
 *
 * The seat map has to be inlined rather than shown in an <img>, because the
 * whole point is colouring blocks by availability and knowing which one was
 * tapped — neither is possible inside an image.
 *
 * Inlining foreign markup is stored XSS unless it is cleaned. An SVG is not a
 * picture format; it is a document that can carry <script>, onclick handlers,
 * javascript: URLs, <foreignObject> containing arbitrary HTML, and external
 * references that phone home. Any club admin who can upload one could
 * otherwise run code in every supporter's browser — including on the checkout.
 *
 * This is an allow-list: anything not named is dropped. A deny-list of known
 * bad tags is the version that gets bypassed.
 *
 * It is the SECOND line of defence. Uploads must be sanitised server-side as
 * well — this only protects the browser that renders, and never the database.
 */

/** Elements a seating plan legitimately needs. */
const ALLOWED_TAGS = new Set([
	"svg", "g", "defs", "title", "desc", "rect", "circle", "ellipse", "line",
	"polyline", "polygon", "path", "text", "tspan", "use", "symbol",
	"linearGradient", "radialGradient", "stop", "clipPath", "mask", "pattern"
]);

/** Attributes worth keeping. Presentation and geometry only. */
const ALLOWED_ATTRS = new Set([
	"id", "class", "d", "x", "y", "x1", "y1", "x2", "y2", "cx", "cy", "r", "rx", "ry",
	"width", "height", "viewBox", "points", "transform", "fill", "fill-opacity",
	"fill-rule", "stroke", "stroke-width", "stroke-opacity", "stroke-linecap",
	"stroke-linejoin", "stroke-dasharray", "opacity", "font-family", "font-size",
	"font-weight", "text-anchor", "dominant-baseline", "letter-spacing",
	"gradientUnits", "offset", "stop-color", "stop-opacity", "clip-path", "mask",
	"pointer-events", "role", "aria-label", "xmlns", "preserveAspectRatio"
]);

/**
 * Strip anything that could execute or fetch.
 *
 * Returns null when the input isn't an SVG at all, which is the honest answer
 * for a club that uploaded a PDF.
 */
export function sanitiseSvg(source: string): string | null {
	if (!source || !source.includes("<svg")) return null;

	let doc: Document;
	try {
		// Parsed as XML, not HTML. An HTML parser is lenient in ways that let
		// malformed markup mean different things to it and to the browser that
		// eventually renders — which is where sanitiser bypasses live.
		doc = new DOMParser().parseFromString(source, "image/svg+xml");
	} catch {
		return null;
	}

	if (doc.querySelector("parsererror")) return null;

	const root = doc.documentElement;
	if (!root || root.nodeName.toLowerCase() !== "svg") return null;

	const clean = (node: Element) => {
		for (const child of Array.from(node.children)) {
			if (!ALLOWED_TAGS.has(child.nodeName)) {
				child.remove();
				continue;
			}
			clean(child);
		}

		for (const attr of Array.from(node.attributes)) {
			const name = attr.name;
			const value = attr.value || "";

			// Every on* handler, whatever its case.
			if (/^on/i.test(name)) {
				node.removeAttribute(name);
				continue;
			}

			if (!ALLOWED_ATTRS.has(name)) {
				node.removeAttribute(name);
				continue;
			}

			// javascript:, data: and anything else that isn't a plain reference.
			// Kept deliberately strict: a seating plan has no business linking
			// anywhere at all.
			if (/(javascript|data|vbscript)\s*:/i.test(value.replace(/\s/g, ""))) {
				node.removeAttribute(name);
			}
		}
	};

	clean(root);

	// Scale to its container rather than whatever the club drew it at.
	root.setAttribute("width", "100%");
	root.removeAttribute("height");
	if (!root.getAttribute("preserveAspectRatio")) {
		root.setAttribute("preserveAspectRatio", "xMidYMid meet");
	}

	return new XMLSerializer().serializeToString(root);
}
